/**
 * 栃木県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「耳うどん」は名にうどんとつくが麺ではない。マッチ箱ほどの長方形に切った生地を
 *   二つ折りにして両端を合わせた、耳の形の小さな塊である。他県のうどんとは絵が別物。
 * - いちごは福岡の「あまおう」、佐賀の「いちごさん」と題材が重なるので入れていない。
 *   栃木の「57年連続日本一」は収穫量の話なので、景色や豆知識で扱うほうが誤解が少ない。
 * - 「どじょう汁」は魚が丸ごと見えて絵が生々しくなるため、汁物の枠は干瓢の卵とじにした。
 * - 「板台餅」は出典に「円盤状に丸めて焼いた」と「俵型に成形」の両方が書かれている。
 *   円盤のほうを採った。
 * - 「揚げまき湯波」は京都の湯葉と違い、二重で引き上げるので厚みがある。日光では「湯波」と書く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：小麦まんじゅう・ゆず巻き・揚げまき湯波の煮物・板台餅・芋ぐし・あいその甘露煮・えび大根・干瓢の卵とじ・しもつかれ（店で食べられる品に差し替え）
 * - 宇都宮餃子：「年間購入額日本一」は年によって順位が変わる（市のページでは2010年まで15年連続、その後2013・2017・2019年に日本一）ため、図鑑の常設文には入れなかった
 * - 宇都宮餃子：「宇都宮餃子は加盟店だけが名乗れる登録商標」は、餃子会のページで「登録商標『宇都宮餃子』の管理」までしか確かめられなかったため書かない
 * - 宇都宮餃子：特定の店名は書かない
 * - 佐野ラーメン：観光協会ページ題名の「日本ご当地ラーメン総選挙で優勝」は、催しの順位で恒常的な事実ではないため書かない
 * - 佐野ラーメン：チャーシュー・メンマ・なるとなどの具は公式ページに記載がないため書かない（絵にはある）
 * - 佐野ラーメン：「平打ち麺」は公式ページで確認できなかったため書かない（観光協会の表現は『不揃いのちぢれ麺』）
 * - 塩原のスープ入り焼きそば：「元祖の店」は店の自称・口コミで、公式で裏が取れないため書かない
 * - 塩原のスープ入り焼きそば：発祥の年は公式に記載がない（『50年以上』まで）ため書かない
 * - 塩原のスープ入り焼きそば：キャベツ・豚肉などの具は公式ページに記載がないため書かない（絵にはある）
 * - 佐野のいもフライ：発祥の年・考案者は公式で確かめられないため書かない
 * - とちぎのいちご：パフェという食べ方は公式資料で確かめられなかったため、説明文はいちごそのものの事実にとどめた
 * - とちぎのいちご：収穫量の数字は毎年更新される。令和6年産（県ページの確報）の時点の数字として書いた
 * - 日光ゆば御膳（日光の湯波料理）：日光市観光協会の別ページにある「約1200年前に輪王寺の僧侶が食べ始めた」は年代のはっきりしない起源話なので書かない
 * - 日光ゆば御膳（日光の湯波料理）：店名（「元祖」を名乗る店など）は書かない
 * - 那珂川のやなの鮎料理：「那珂川は鮎の漁獲高日本一」はやな施設（民間）のサイトの記述で、県・国の資料で確かめられなかったため書かない
 * - 那珂川のやなの鮎料理：「やな漁は300年以上の歴史」は公式で確かめられなかったため書かない
 * - 那珂川のやなの鮎料理：やなの開設期間は施設ごと・年ごとに変わる（県の特集では6月上旬〜10月下旬など）ため、県ページの『7月から10月』にとどめた
 * - 中禅寺湖のヒメマスの塩焼き：「明治39年（1906年）に十和田湖から放流」は検索結果に出たが、開いた公式ページでは確かめられなかったため書かない
 * - 中禅寺湖のヒメマスの塩焼き：「中禅寺湖のヒメマス釣りは日本一」は日光市観光協会の釣り情報ページにあるが、料理の説明ではないので使わない
 * - とちぎ和牛：「前沢牛や松阪牛にも劣らない」は他の銘柄との比較なので書かない
 * - とちぎ和牛：マーケティング協会の「『日本一』の銘柄牛と称される」は団体の自称に近い大きな主張なので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { tochigiSceneryTour } from './tochigiScenes';
import { TOCHIGI_FOOD_HULLS } from './tochigiFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'kanpyo-makizushi',
    level: 1,
    name: 'かんぴょうのり巻き',
    shortName: 'かんぴょうのり巻き',
    image: '/assets/foods/tochigi/01_kanpyo_makizushi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: 'ゆうがおの果肉をひも状にむいて乾かしたかんぴょうを煮て、酢飯とともに海苔で巻く。かんぴょうの生産は江戸時代の正徳2年（1712年）、鳥居忠英が江州から壬生へ国替えになった際にゆうがおの種を取り寄せたのが始まりとされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/31_18_tochigi.html',
    icon: { label: 'かんぴょうのり巻き', shape: 'mochi', baseColor: '#2b2b28', accentColor: '#8a5a24' },
    reward: {
      unlockScore: 200,
      area: '壬生町',
      headline: '全国の9割以上が栃木産',
      tip: 'かんぴょうは全国の約90%以上の生産を栃木県が占める。ユウガオの実を細長くむいて竿に干し、真夏の太陽で一気に干し上げる。',
      links: searchLinks('壬生町', 'かんぴょう巻き'),
    },
  },
  {
    id: 'utsunomiya-gyoza',
    level: 2,
    name: '宇都宮餃子',
    shortName: '宇都宮餃子',
    image: '/assets/foods/tochigi/02_utsunomiya_gyoza.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '焼き・揚げ・水などの種類があり、大きさや素材、皮の厚さ、包み方、はねの大きさ、たれまで店ごとに違う。市内に駐屯していた第14師団が中国で餃子を知り、帰郷後に広めたのがきっかけとされる。',
    source:
      'https://www.city.utsunomiya.lg.jp/citypromotion/1007188.html',
    icon: { label: '餃子', shape: 'dumpling', baseColor: '#e2b46a', accentColor: '#8a5a2b' },
    reward: {
      unlockScore: 500,
      area: '宇都宮市',
      headline: '店ごとに味がちがう餃子のまち',
      tip: '夏は暑く冬は寒い内陸の気候で、スタミナをつけるために人気が高まったとされる。1993年に市内の専門店など38店で宇都宮餃子会が発足した。',
      links: searchLinks('宇都宮市', '宇都宮餃子'),
      official: [{ label: '宇都宮市「餃子のまち」', url: 'https://www.city.utsunomiya.lg.jp/citypromotion/1007188.html' }, { label: '宇都宮餃子会「宇都宮餃子会とは」', url: 'https://www.gyozakai.com/about/' }],
    },
  },
  {
    id: 'sano-ramen',
    level: 3,
    name: '佐野ラーメン',
    shortName: '佐野ラーメン',
    image: '/assets/foods/tochigi/03_sano_ramen.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '佐野の水で作るしょうゆ味のあっさりしたスープに、青竹で打った不揃いのちぢれ麺を合わせる。麺の内部に気泡が多いので熱がよく通り、短い時間でゆで上がって腰の強い舌ざわりになる。',
    source:
      'https://sano-kankokk.jp/?page_id=3418',
    icon: { label: 'ラーメン', shape: 'ramen', baseColor: '#c98a3a', accentColor: '#f2e3b3' },
    reward: {
      unlockScore: 1000,
      area: '佐野市',
      headline: '青竹で打つちぢれ麺',
      tip: '大正時代に中国の料理人が青竹打ちを伝えたのがルーツといわれる。市内には100年以上続く老舗をはじめ150以上のらーめん店がある。',
      links: searchLinks('佐野市', '佐野ラーメン'),
      official: [{ label: '佐野市「佐野らーめん」', url: 'https://www.city.sano.lg.jp/soshikiichiran/sougou/toshibrandsuishin/gyomuannai/koho/meibutsu/5152.html' }, { label: '佐野市観光協会「佐野らーめん」', url: 'https://sano-kankokk.jp/?page_id=3418' }],
    },
  },
  {
    id: 'mimi-udon',
    level: 4,
    name: '耳うどん',
    shortName: '耳うどん',
    image: '/assets/foods/tochigi/04_mimi_udon.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '練った小麦粉をマッチ箱ほどの長方形に切り、二つ折りにしながら内側の両端を合わせて閉じる。その形が耳に似ることから名がついた。麺ではない。佐野では「いい耳聞け」と祈る風習がある。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/31_2_tochigi.html',
    icon: { label: '耳うどん', shape: 'dumpling', baseColor: '#f4efe0', accentColor: '#d8cbb0' },
    reward: {
      unlockScore: 1800,
      area: '佐野市葛生地区',
      headline: 'うどんという名の耳',
      tip: '宇都宮市では星野宮神社の氏子のあいだで、正月の祭礼のごちそうとされた。もとは年の暮れに作って正月の来客に振る舞ったが、いまは年中食べられている。',
      links: searchLinks('佐野市', '耳うどん'),
    },
  },
  {
    id: 'shiobara-soup-yakisoba',
    level: 5,
    name: '塩原のスープ入り焼きそば',
    shortName: 'スープ入り焼きそば',
    image: '/assets/foods/tochigi/05_shiobara_soup_yakisoba.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '炒めたてのソース焼きそばをどんぶりに入れ、しょうゆ味のスープを注いで仕上げる。見た目はラーメンのようだが、食べると焼きそば。しょうゆ味のスープは少しずつソース味と混ざり、独特の風味に変わっていく。',
    source:
      'https://www.siobara.or.jp/gourmet/yakisoba/',
    icon: { label: '焼きそば', shape: 'noodle-bowl', baseColor: '#8a5a2b', accentColor: '#c9a36a' },
    reward: {
      unlockScore: 3000,
      area: '那須塩原市塩原（塩原温泉）',
      headline: 'スープを注いだ焼きそば',
      tip: '塩原温泉で50年以上親しまれてきた塩原名物で、塩原温泉を語るうえで欠かせない一品とされる。温泉街の食堂などで出している。',
      links: searchLinks('那須塩原市塩原', 'スープ入り焼きそば'),
      official: [{ label: '塩原温泉観光協会「スープ入り焼きそば」', url: 'https://www.siobara.or.jp/gourmet/yakisoba/' }, { label: 'とちぎ旅ネット（栃木県観光物産協会）「スープ入り焼きそば」', url: 'https://www.tochigiji.or.jp/spot/s48570' }],
    },
  },
  {
    id: 'sano-imo-fry',
    level: 6,
    name: '佐野のいもフライ',
    shortName: 'いもフライ',
    image: '/assets/foods/tochigi/06_sano_imo_fry.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '少し堅めに蒸したじゃがいもの皮をむいて切り、竹串に3〜4個刺す。水で溶いた小麦粉の衣とパン粉をつけて小麦色に揚げ、温かいうちにソースをつける。じゃがいもの甘みとほくほく感がソースに合う。',
    source:
      'https://sano-kankokk.jp/?page_id=4046',
    icon: { label: 'いもフライ', shape: 'dumpling', baseColor: '#d9a441', accentColor: '#5a2e1a' },
    reward: {
      unlockScore: 4500,
      area: '佐野市',
      headline: '蒸したじゃがいもを串で揚げる',
      tip: '佐野発祥の懐かしい味とされ、小腹がすいたときのおやつや夕飯のおかずに親しまれている。観光協会は市内の店を集めた「いもフライマップ」を出している。',
      links: searchLinks('佐野市', 'いもフライ'),
      official: [{ label: '佐野市「いもフライ」', url: 'https://www.city.sano.lg.jp/soshikiichiran/sougou/toshibrandsuishin/gyomuannai/koho/meibutsu/w10313.html' }, { label: '佐野市観光協会「いもフライ」', url: 'https://sano-kankokk.jp/?page_id=4046' }],
    },
  },
  {
    id: 'moro-nitsuke',
    level: 7,
    name: 'モロの煮付',
    shortName: 'モロの煮付',
    image: '/assets/foods/tochigi/07_moro_nitsuke.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '「モロ」はネズミザメのこと。醤油と砂糖とみりんと酒の煮汁を煮立てた鍋に切り身としょうがを入れ、中火で10分ほど煮含める。器に盛って千切りのしょうがを飾る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/31_10_tochigi.html',
    icon: { label: 'モロの煮付', shape: 'curry-plate', baseColor: '#8a5a2a', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 6500,
      area: '県内全域',
      headline: '海のない県のサメ料理',
      tip: '内陸でサメが重宝されたのは、サメが体の中に尿酸を蓄えており、命が尽きると尿酸が分散してアンモニアになり腐りにくくなるため。4月から7月に旬を迎えるが、一年中手に入る。',
      links: searchLinks('宇都宮市', 'モロの煮付'),
    },
  },
  {
    id: 'tochigi-ichigo',
    level: 8,
    name: 'とちぎのいちご',
    shortName: 'とちぎのいちご',
    image: '/assets/foods/tochigi/08_tochigi_ichigo.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '栃木県のいちごは、収穫量が昭和43年産から令和6年産まで57年連続で日本一と県が発表している。県内では「とちおとめ」「スカイベリー」「とちあいか」「ミルキーベリー」などの品種が作られている。',
    source:
      'https://www.pref.tochigi.lg.jp/g61/ichigo-seisanjokyo/ichigo-seisanryou.html',
    icon: { label: 'いちご', shape: 'berry', baseColor: '#d7263d', accentColor: '#fff4e6' },
    reward: {
      unlockScore: 9000,
      area: '栃木県全域',
      headline: '栃木県の発表で、収穫量57年連続日本一のいちご',
      tip: '令和6年産の収穫量は25,700トン。県の発表では、作付面積も平成13年産から24年連続、産出額も平成7年から30年連続で日本一。',
      links: searchLinks('栃木県', 'いちごパフェ'),
      official: [{ label: '栃木県「全国のいちご生産割合から見た栃木県の状況」', url: 'https://www.pref.tochigi.lg.jp/g61/ichigo-seisanjokyo/ichigo-seisanryou.html' }],
    },
  },
  {
    id: 'nikko-yuba-ryori',
    level: 9,
    name: '日光ゆば御膳（日光の湯波料理）',
    shortName: '日光湯波',
    image: '/assets/foods/tochigi/09_nikko_yuba_ryori.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '国産大豆と日光の水で作る豆乳から、表面の膜を二重に引き上げて作る。一重で薄く引き上げる京都の「湯葉」に対し、日光では「湯波」と書き、厚みのある湯波を料理の主役に使う。生湯波は刺身で食べる。',
    source:
      'https://www.nikko-kankou.org/features/59',
    icon: { label: '湯波', shape: 'feast', baseColor: '#f1dfb0', accentColor: '#2b2b2b' },
    reward: {
      unlockScore: 12000,
      area: '日光市',
      headline: '二重に引き上げる日光の湯波',
      tip: '修験道の霊場である日光山で、栄養があり消化のよいゆばは修行者に欠かせない食べ物だった。のちに寺社への供物や精進料理の材料として広まった。',
      links: searchLinks('日光市', '湯波料理'),
      official: [{ label: '日光市観光協会「「湯葉」と「湯波」の違いは？」', url: 'https://www.nikko-kankou.org/features/59' }, { label: '栃木県「日光湯波」', url: 'https://www.pref.tochigi.lg.jp/c05/intro/tochigiken/miryoku/nikko/016.html' }],
    },
  },
  {
    id: 'nakagawa-yana-ayu',
    level: 10,
    name: '那珂川のやなの鮎料理',
    shortName: 'やなの鮎',
    image: '/assets/foods/tochigi/10_nakagawa_yana_ayu.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '川の流れの一部をV字にせき止めて水を集め、その中央の床に張った簾で魚を捕らえる漁法を「やな」という。県内の川に掛けられる観光やなでは、鮎のつかみ取りのほか、塩焼きや鮎飯などの鮎料理が味わえる。',
    source:
      'https://www.pref.tochigi.lg.jp/c05/intro/tochigiken/miryoku/nasu/009.html',
    icon: { label: '鮎', shape: 'fish', baseColor: '#9aa7a8', accentColor: '#4a3a2a' },
    reward: {
      unlockScore: 16000,
      area: '那珂川流域',
      headline: 'やなで捕れた鮎を塩焼きに',
      tip: '観光やなは7月から10月にかけて、那珂川や鬼怒川に掛けられる。那珂川は「西の四万十川、東の那珂川」と称される清流で、鮎釣りのメッカとして知られる。',
      links: searchLinks('那珂川', '観光やな 鮎塩焼き'),
      official: [{ label: '栃木県「観光やな」', url: 'https://www.pref.tochigi.lg.jp/c05/intro/tochigiken/miryoku/nasu/009.html' }, { label: '栃木県農村振興課「とちぎの観光やな特集！」', url: 'https://www.agrinet.pref.tochigi.lg.jp/tochigi-nouson-meguri/yana-sp' }],
    },
  },
  {
    id: 'chuzenji-himemasu',
    level: 11,
    name: '中禅寺湖のヒメマスの塩焼き',
    shortName: 'ヒメマスの塩焼き',
    image: '/assets/foods/tochigi/11_chuzenji_himemasu.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'ヒメマスはベニザケと同じ種類で、海に下らず湖で育つサケ科の魚。中禅寺湖は全国でも有数の産地で、身は紅色。主に塩焼きやムニエルにして食べ、甘露煮や燻製にも加工される。',
    source:
      'https://tochigipower.com/?class=catalog&page=703',
    icon: { label: 'ヒメマス', shape: 'fish', baseColor: '#c9c2b8', accentColor: '#e48a6a' },
    reward: {
      unlockScore: 22000,
      area: '日光市（中禅寺湖）',
      headline: '湖で育つ紅い身のマス',
      tip: '4月から9月の解禁期間には多くの釣り人でにぎわう。食べごろは200〜300グラムほどとされる。',
      links: searchLinks('日光市 中禅寺湖', 'ヒメマス 塩焼き'),
      official: [{ label: '日光市観光協会「中禅寺湖＜釣り情報＞」', url: 'https://www.nikko-kankou.org/spot/360' }],
    },
  },
  {
    id: 'tochigi-wagyu',
    level: 12,
    name: 'とちぎ和牛',
    shortName: 'とちぎ和牛',
    image: '/assets/foods/tochigi/12_tochigi_wagyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '血統の優れた黒毛和種の子牛を、指定生産農家が一頭ずつ育てた肉質のよい肉牛。やわらかく味のよい霜降り肉になる。昭和63年、県産和牛のブランド化に取り組むなかで「とちぎ和牛」と名付けられた。',
    source:
      'https://www.pref.tochigi.lg.jp/c05/kensei/aramashi/sangyou/nouringyou/wagyuu.html',
    icon: { label: '和牛', shape: 'feast', baseColor: '#b5473a', accentColor: '#f3d9c8' },
    reward: {
      unlockScore: 30000,
      area: '栃木県全域',
      headline: '指定農家が育てる霜降り牛',
      tip: '品質の格付が上位の、格付等級A・Bの4・5等級に入るものだけが「とちぎ和牛」と呼ばれる。',
      links: searchLinks('栃木県', 'とちぎ和牛'),
      official: [{ label: '栃木県「とちぎ和牛」', url: 'https://www.pref.tochigi.lg.jp/c05/kensei/aramashi/sangyou/nouringyou/wagyuu.html' }, { label: 'とちぎ農産物マーケティング協会「とちぎ和牛」', url: 'https://tochigipower.com/?class=catalog&page=601' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const tochigiFoodStage: StageDefinition = {
  regionId: 'tochigi',
  regionName: '栃木県',
  mode: 'food',
  title: '栃木めしマージ',
  theme: {
    primaryColor: '#b8332a',
    secondaryColor: '#efe3d4',
    accentColor: '#2f6b4f',
    backgroundColor: '#faf5ec',
  },
  /** 背景ツアー「栃木めぐり」。軸は季節。4月末の足利のフジから始めて、3月の足利学校で終わる。奥日光と県南を行き来する。 */
  scenery: {
    tour: tochigiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, TOCHIGI_FOOD_HULLS),
};
